import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CURRENCIES, type Currency } from '@/shared/currencies';
import { useI18n } from '@/shared/i18n/useT';

// Chips for the picked currencies, a searchable checklist below them. With `max` the
// picks keep the order they were made in (the first one is the primary currency) and
// the unpicked options lock once the limit is reached; without it they follow CURRENCIES.
export function CurrencyMultiSelect({
  value,
  onChange,
  disabled = false,
  options = CURRENCIES,
  max,
  compact = false,
  label,
}: {
  value: readonly Currency[];
  onChange: (next: Currency[]) => void;
  disabled?: boolean;
  options?: readonly Currency[];
  max?: number;
  compact?: boolean;
  label?: string;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  // The list is portaled to the body: toolbars clip and stack over anything that
  // escapes them, so it follows the button with fixed positioning instead.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => setAnchor(root.current?.getBoundingClientRect() ?? null);
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!root.current?.contains(target) && !popup.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const shown = options.filter((currency) => currency.includes(query.trim().toUpperCase()));
  const full = max !== undefined && value.length >= max;
  const toggle = (currency: Currency) => {
    if (max !== undefined) {
      onChange(value.includes(currency) ? value.filter((c) => c !== currency) : [...value, currency].slice(0, max));
      return;
    }
    onChange(options.filter((c) => (c === currency ? !value.includes(c) : value.includes(c))));
  };

  return (
    <div ref={root} className={compact ? 'relative shrink-0' : 'relative'}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => {
          setQuery('');
          setOpen((o) => !o);
        }}
        className={compact
          ? 'flex h-7 items-center gap-1 rounded-lg border border-border-strong bg-bg px-1.5 text-left text-xs disabled:opacity-60'
          : 'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border border-border-strong bg-bg px-2 py-1.5 text-left text-sm disabled:opacity-60'}
      >
        {value.length === 0 ? (
          <span className="px-1 text-muted">{t('settings.grid.formats.currenciesNone')}</span>
        ) : (
          value.map((currency) => (
            <span key={currency} className="rounded-full bg-surface-hover px-2 py-0.5 text-xs font-medium">
              {currency}
            </span>
          ))
        )}
      </button>
      {open && anchor && createPortal(
        <div
          ref={popup}
          style={{ position: 'fixed', top: anchor.bottom + 4, left: anchor.left, width: compact ? Math.max(192, anchor.width) : anchor.width }}
          className="z-50 rounded-lg border border-border-strong bg-bg shadow-lg"
        >
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('settings.grid.formats.currenciesSearch')}
            aria-label={t('settings.grid.formats.currenciesSearch')}
            className="w-full border-b border-border bg-transparent px-3 py-2 text-sm outline-none"
          />
          <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1">
            {shown.map((currency) => {
              const checked = value.includes(currency);
              const locked = full && !checked;
              return (
                <li key={currency}>
                  <label className={`flex items-center gap-3 px-3 py-1.5 text-sm ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-surface-hover'}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={() => toggle(currency)}
                      className="h-4 w-4 accent-accent"
                    />
                    <span className="rounded-full bg-surface-hover px-2 py-0.5 text-xs font-medium">{currency}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
